'use client';

import type { ECElementEvent } from 'echarts';
import type ReactEChartsCore from 'echarts-for-react/lib/core';
import { useEffect, useRef } from 'react';
import { Chart } from '@/components/Chart';
import { StatsByRound } from '@/lib/query';

export const CumulativeScore = ({
  statsByRound,
  onSetRound,
  isMobile,
}: {
  statsByRound: StatsByRound;
  onSetRound: (round: number) => void;
  isMobile?: boolean;
}) => {
  const data = statsByRound.map((roundStats, idx) => ({
    name: isMobile ? `[${idx + 1}]` : `[${idx + 1}] ${roundStats.roundName}`,
    score: roundStats.cumulativeScore,
  }));

  const chartRef = useRef<ReactEChartsCore>(null);
  useEffect(() => {
    if (!chartRef.current) return;

    const chart = chartRef.current.getEchartsInstance();

    const handleClick = (params: ECElementEvent) => {
      const { componentType, dataIndex } = params;
      if (componentType === 'series') {
        onSetRound(dataIndex);
      }
    };

    chart.on('click', handleClick);

    return () => {
      chart.off('click', handleClick);
    }
  }, [onSetRound]);

  return (
    <Chart
      ref={chartRef}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        grid: {
          top: '32px',
          bottom: '32px',
          left: '32px',
          right: '32px',
        },
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999'
            }
          }
        },
        xAxis: {
          type: 'category',
          data: data.map((d) => d.name),
          axisPointer: {
            type: 'shadow',
          },
          axisLabel: {
            hideOverlap: isMobile ? true : false,
            overflow: 'truncate',
            interval: 0,
            width: isMobile ? undefined : 760 / statsByRound.length,
            color: '#a1a1aa',
          },
          boundaryGap: false,
          axisLine: {
            lineStyle: {
              color: '#4a4a4a',
            },
          },
        },
        yAxis: {
          type: 'value',
          axisPointer: {
            type: 'shadow',
          },
          splitNumber: 3,
          splitLine: {
            lineStyle: {
              color: '#27272a',
            },
          },
        },
        series: [
          {
            name: 'score',
            type: 'line',
            smooth: true,
            areaStyle: {},
            data: data.map((d) => d.score),
          },
        ],
      }}
    />
  )
};
